import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { shopApi } from '../../api/shop';
import { useAuth } from '../../auth/AuthProvider';
import { AddressFormFields } from '../../components/AddressFormFields';
import { RequireShop } from '../../components/ShopSelector';
import { Breadcrumb, SectionCard, TabBar } from '../../components/PageChrome';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';
import { useShop } from '../../context/ShopContext';
import { emptyAddress } from '../../utils/format';

const emptyCustomerForm = {
  first_name: '',
  last_name: '',
  phone: '',
  notes: '',
  is_active: true,
};

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { selectedShopId } = useShop();
  const [customer, setCustomer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [tab, setTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [profileForm, setProfileForm] = useState(emptyCustomerForm);
  const [addressModal, setAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddress);

  const loadCustomer = useCallback(async () => {
    if (!selectedShopId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [customerData, addressData] = await Promise.all([
        shopApi.getCustomer(token, selectedShopId, customerId),
        shopApi.listCustomerAddresses(token, selectedShopId, customerId),
      ]);
      setCustomer(customerData);
      setAddresses(addressData);
      setProfileForm({
        first_name: customerData.first_name,
        last_name: customerData.last_name,
        phone: customerData.phone || '',
        notes: customerData.notes || '',
        is_active: customerData.is_active,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId, getToken, selectedShopId]);

  useEffect(() => {
    loadCustomer();
  }, [loadCustomer]);

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const updated = await shopApi.updateCustomer(token, selectedShopId, customerId, profileForm);
      setCustomer(updated);
      setSuccess('Customer profile updated.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAddress = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      await shopApi.createCustomerAddress(token, selectedShopId, customerId, {
        ...addressForm,
        label: addressForm.label || null,
        company: addressForm.company || null,
        phone: addressForm.phone || null,
        email: addressForm.email || null,
        line2: addressForm.line2 || null,
        state: addressForm.state || null,
      });
      setAddressModal(false);
      setAddressForm(emptyAddress);
      await loadCustomer();
      setSuccess('Address added.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    setError(null);
    try {
      const token = await getToken();
      await shopApi.deleteCustomerAddress(token, selectedShopId, customerId, addressId);
      await loadCustomer();
      setSuccess('Address removed.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  if (!customer) {
    return <ErrorBanner message={error || 'Customer not found.'} />;
  }

  return (
    <RequireShop>
      <Breadcrumb
        items={[
          { label: 'Customers', to: '/shop/customers' },
          { label: `${customer.first_name} ${customer.last_name}` },
        ]}
      />

      <PageHeader
        title={`${customer.first_name} ${customer.last_name}`}
        description={customer.email}
        actions={
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/shop/customers')}>
            Back to customers
          </button>
        }
      />

      <ErrorBanner message={error} />
      {success && <div className="success-banner">{success}</div>}

      <TabBar
        tabs={[
          { id: 'profile', label: 'Profile' },
          { id: 'addresses', label: `Addresses (${addresses.length})` },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'profile' && (
        <SectionCard title="Customer profile">
          <form className="form-grid" onSubmit={handleSaveProfile}>
            <div className="form-grid two-col">
              <div className="form-field">
                <label htmlFor="cust-first">First name</label>
                <input
                  id="cust-first"
                  value={profileForm.first_name}
                  onChange={(e) => setProfileForm((p) => ({ ...p, first_name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="cust-last">Last name</label>
                <input
                  id="cust-last"
                  value={profileForm.last_name}
                  onChange={(e) => setProfileForm((p) => ({ ...p, last_name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="form-field">
              <label htmlFor="cust-phone">Phone</label>
              <input
                id="cust-phone"
                value={profileForm.phone}
                onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label htmlFor="cust-notes">Notes</label>
              <textarea
                id="cust-notes"
                rows={3}
                value={profileForm.notes}
                onChange={(e) => setProfileForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={profileForm.is_active}
                onChange={(e) => setProfileForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
              Active customer
            </label>
            <div className="page-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save profile'}
              </button>
              <Link to="/shop/orders/new" className="btn btn-secondary">
                Create order
              </Link>
            </div>
          </form>
        </SectionCard>
      )}

      {tab === 'addresses' && (
        <SectionCard
          title="Saved addresses"
          description="Used for checkout and order fulfillment."
          actions={
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAddressModal(true)}>
              Add address
            </button>
          }
        >
          {addresses.length === 0 ? (
            <EmptyState message="No saved addresses for this customer." />
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Address</th>
                  <th>Defaults</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {addresses.map((address) => (
                  <tr key={address.id}>
                    <td>{address.label || '—'}</td>
                    <td>
                      <strong>{address.full_name}</strong>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        {address.line1}
                        {address.line2 ? `, ${address.line2}` : ''}, {address.city}{' '}
                        {address.postal_code}, {address.country_code}
                      </div>
                    </td>
                    <td>
                      {address.is_default_shipping && (
                        <span className="badge badge-info" style={{ marginRight: '0.25rem' }}>
                          Shipping
                        </span>
                      )}
                      {address.is_default_billing && (
                        <span className="badge badge-info">Billing</span>
                      )}
                      {!address.is_default_shipping && !address.is_default_billing && '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteAddress(address.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      )}

      <Modal
        title="Add address"
        open={addressModal}
        onClose={() => setAddressModal(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setAddressModal(false)}>
              Cancel
            </button>
            <button type="submit" form="address-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save address'}
            </button>
          </>
        }
      >
        <form id="address-form" className="form-grid" onSubmit={handleCreateAddress}>
          <AddressFormFields form={addressForm} onChange={setAddressForm} idPrefix="new" />
        </form>
      </Modal>
    </RequireShop>
  );
}
